import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api, qs } from "./client.js";
import type {
  ApplicationInfo,
  AuditResponse,
  ImportResult,
  PropertiesResponse,
  PropertyEntry,
  SaveResult,
  StatusResponse,
} from "./types.js";

export function useApplications(filter = "") {
  return useQuery({
    queryKey: ["applications", filter],
    queryFn: () =>
      api.get<{ applications: ApplicationInfo[] }>(`/api/applications${qs({ filter })}`),
    select: (data) => data.applications,
  });
}

export function useTargets(application: string) {
  return useQuery({
    queryKey: ["targets", application],
    queryFn: () => api.get<{ targets: string[] }>(`/api/targets${qs({ application })}`),
    select: (data) => data.targets,
    enabled: application !== "",
  });
}

export interface PropertiesParams {
  application: string;
  name: string;
  target: string;
  value: string;
  page: number;
  pageSize: number;
}

export function useProperties(params: PropertiesParams) {
  return useQuery({
    queryKey: ["properties", params],
    queryFn: () =>
      api.get<PropertiesResponse>(
        `/api/properties${qs({
          application: params.application,
          name: params.name,
          target: params.target,
          value: params.value,
          preview: 120,
          limit: params.pageSize,
          offset: (params.page - 1) * params.pageSize,
        })}`,
      ),
    enabled: params.application !== "",
    placeholderData: keepPreviousData,
  });
}

export function usePropertyEntries(application: string, name: string | null) {
  return useQuery({
    queryKey: ["entries", application, name],
    queryFn: () =>
      api.get<{ entries: PropertyEntry[] }>(
        `/api/property/entries${qs({ application, name: name ?? "" })}`,
      ),
    select: (data) => data.entries,
    enabled: application !== "" && name !== null,
  });
}

export function useAuditLog(params: {
  application: string;
  name: string;
  username: string;
  action: string;
  page: number;
  pageSize: number;
}) {
  return useQuery({
    queryKey: ["audit", params],
    queryFn: () =>
      api.get<AuditResponse>(
        `/api/audit${qs({
          application: params.application,
          name: params.name,
          username: params.username,
          action: params.action,
          limit: params.pageSize,
          offset: (params.page - 1) * params.pageSize,
        })}`,
      ),
    placeholderData: keepPreviousData,
  });
}

export function useStatus() {
  return useQuery({
    queryKey: ["status"],
    queryFn: () => api.get<StatusResponse>("/api/status"),
    refetchInterval: 10_000,
    retry: false,
  });
}

function useInvalidateConfigData() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["properties"] });
    void queryClient.invalidateQueries({ queryKey: ["entries"] });
    void queryClient.invalidateQueries({ queryKey: ["applications"] });
    void queryClient.invalidateQueries({ queryKey: ["targets"] });
    void queryClient.invalidateQueries({ queryKey: ["audit"] });
    void queryClient.invalidateQueries({ queryKey: ["status"] });
  };
}

export interface SavePropertyInput {
  application: string;
  name: string;
  target: string;
  value: string;
  controlUpdateId: number;
  message?: string;
  description?: string;
}

export function useSaveProperty() {
  const invalidate = useInvalidateConfigData();
  return useMutation({
    mutationFn: (input: SavePropertyInput) => api.put<SaveResult>("/api/property", input),
    onSuccess: invalidate,
  });
}

export interface DeletePropertyInput {
  application: string;
  name: string;
  target: string;
  controlUpdateId: number;
  message?: string;
}

export function useDeleteProperty() {
  const invalidate = useInvalidateConfigData();
  return useMutation({
    mutationFn: (input: DeletePropertyInput) =>
      api.delete<SaveResult>(
        `/api/property${qs({
          application: input.application,
          name: input.name,
          target: input.target,
          controlUpdateId: input.controlUpdateId,
          message: input.message,
        })}`,
      ),
    onSuccess: invalidate,
  });
}

export interface BulkDeleteInput {
  application: string;
  entries: { name: string; target: string }[];
  message?: string;
}

export interface BulkDeleteResult {
  deleted: number;
  failed: { name: string; target: string; error: string }[];
}

export function useBulkDeleteProperties() {
  const invalidate = useInvalidateConfigData();
  return useMutation({
    mutationFn: (input: BulkDeleteInput) => api.post<BulkDeleteResult>("/api/properties/delete", input),
    onSuccess: invalidate,
  });
}

export interface ImportInput {
  application: string;
  mode: "create" | "overwrite";
  message?: string;
  entries: { name: string; target: string; value: string; description?: string }[];
}

export function useImportProperties() {
  const invalidate = useInvalidateConfigData();
  return useMutation({
    mutationFn: (input: ImportInput) => api.post<ImportResult>("/api/import", input),
    onSuccess: invalidate,
  });
}

export function useSnapshot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ updateId: number; file: string }>("/api/snapshot"),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["status"] }),
  });
}
